import { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';

const ADD_CONTACT = gql`
  mutation AddContact($input: AddContactInput!) {
    addContact(input: $input) {
      id
      name
      phone
      email
    }
  }
`;

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: Int!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      name
      phone
      email
    }
  }
`;

const GET_GROUPS = gql`
  query GetGroups {
    contactGroups {
      id
      name
    }
  }
`;

const CREATE_GROUP = gql`
  mutation CreateGroup($name: String!) {
    createContactGroup(name: $name) {
      id
      name
    }
  }
`;

function AddContactModal({ onClose, onSuccess, editingContact }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    groupId: '',
    notes: '',
  });
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [error, setError] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (editingContact) {
      setFormData({
        name: editingContact.name || '',
        phone: editingContact.phone || '',
        email: editingContact.email || '',
        groupId: editingContact.groups?.[0]?.id || '',
        notes: editingContact.notes || '',
      });
    }
  }, [editingContact]);

  const { data: groupsData } = useQuery(GET_GROUPS);

  const [addContact, { loading }] = useMutation(ADD_CONTACT, {
    onCompleted: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const [updateContact, { loading: updateLoading }] = useMutation(UPDATE_CONTACT, {
    onCompleted: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const [createGroup] = useMutation(CREATE_GROUP, {
    onCompleted: (data) => {
      setFormData({ ...formData, groupId: data.createContactGroup.id });
      setShowNewGroup(false);
      setNewGroupName('');
    },
    refetchQueries: [{ query: GET_GROUPS }],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Designation required');
      return;
    }

    if (!formData.phone.trim()) {
      setError('Network identifier required');
      return;
    }

    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setError('Invalid network identifier length');
      return;
    }

    const input = {
      name: formData.name,
      phone: cleanPhone,
      email: formData.email || null,
      groupId: formData.groupId ? parseInt(formData.groupId) : null,
      notes: formData.notes || null,
    };

    if (editingContact) {
      // Update existing contact
      await updateContact({
        variables: {
          id: editingContact.id,
          input,
        },
      });
    } else {
      // Add new contact
      await addContact({
        variables: { input },
      });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setError('Collective designation required');
      return;
    }

    await createGroup({
      variables: { name: newGroupName },
    });
  };

  return (
    <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 md:p-6">
      <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden border border-white/20 animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 p-6 md:p-10 text-white relative shrink-0">
          <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/10 rounded-bl-full animate-pulse"></div>
          <h2 className="font-medium">
            {editingContact ? 'Entity Modification' : 'New Entity Intake'}
          </h2>
          <p className="text-bulky-navy-300 text-[9px] md:text-[10px] font-normal uppercase tracking-[0.2em] mt-2 relative z-10 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-bulky-blue-400"></span>
            {editingContact ? 'Update terminal identity parameters' : 'Register new terminal identity in central matrix'}
          </p>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-white/80 hover:text-white hover:bg-white/10 transition-all z-20"
          >
            <span className="material-icons-round text-xl">close</span>
          </button>
        </div>

        <div className="p-6 md:p-10 overflow-y-auto no-scrollbar">
          {error && (
            <div className="mb-8 p-4 bg-red-50 border-2 border-red-100 text-red-600 rounded-2xl flex items-center gap-3 text-[10px] font-normal uppercase tracking-wider shadow-sm">
              <span className="material-icons-round text-lg">error_outline</span>
              <div>
                <p className="leading-none">Configuration Failure</p>
                <p className="mt-1 opacity-70">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">
                Entity Designation <span className="text-bulky-blue-400">*</span>
              </label>
              <div className="relative group">
                <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-bulky-blue-500 transition-colors">person</span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                  placeholder="e.g., COMMANDER-ALPHA"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">
                  Network Link (Phone) <span className="text-bulky-blue-400">*</span>
                </label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-bulky-blue-500 transition-colors">phone_iphone</span>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="919876543210"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Protocol Address (Email)</label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-bulky-blue-500 transition-colors">alternate_email</span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="entity@network.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Collective Affiliation</label>
              {!showNewGroup ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300">hub</span>
                    <select
                      value={formData.groupId}
                      onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
                      className="w-full pl-14 pr-12 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 appearance-none shadow-sm"
                    >
                      <option value="">Unassigned Segment</option>
                      {groupsData?.contactGroups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                    <span className="material-icons-round absolute right-4 top-1/2 -translate-y-1/2 text-bulky-navy-300 pointer-events-none">expand_more</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewGroup(true)}
                    className="h-[58px] px-6 bg-white border-2 border-gray-100 text-bulky-navy-400 hover:text-bulky-blue-500 hover:border-bulky-blue-100 rounded-2xl transition-all shadow-sm flex items-center justify-center gap-2"
                  >
                    <span className="material-icons-round text-lg">add_circle_outline</span>
                    <span className="sm:hidden text-[10px] font-normal uppercase tracking-wider">New Group</span>
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 animate-in slide-in-from-right-4 duration-300">
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="flex-1 px-6 py-4.5 bg-white border-2 border-bulky-blue-400 rounded-2xl focus:outline-none font-normal text-bulky-navy-800 shadow-lg shadow-bulky-blue-500/10"
                    placeholder="New Collective Name"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateGroup}
                      className="flex-1 sm:flex-none h-[58px] px-6 bg-bulky-blue-500 text-white rounded-2xl hover:bg-bulky-blue-600 shadow-xl shadow-bulky-blue-500/20 active:scale-95 transition-all flex items-center justify-center"
                    >
                      <span className="material-icons-round">check</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewGroup(false); setNewGroupName(''); }}
                      className="flex-1 sm:flex-none h-[58px] px-6 bg-gray-100 text-gray-500 rounded-2xl hover:bg-gray-200 transition-all flex items-center justify-center"
                    >
                      <span className="material-icons-round">close</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Terminal Metadata (Notes)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-6 py-5 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 min-h-[140px] shadow-sm leading-relaxed"
                placeholder="Additional configuration notes..."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100 mt-10">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:flex-1 px-8 py-5 border-2 border-gray-100 text-bulky-navy-500 font-normal rounded-2xl hover:bg-gray-50 transition-all uppercase tracking-wider text-[10px]"
              >
                Terminate
              </button>
              <button
                type="submit"
                disabled={loading || updateLoading}
                className="w-full sm:flex-1 px-8 py-5 bg-bulky-blue-500 text-white font-normal rounded-2xl hover:bg-bulky-blue-600 shadow-xl shadow-bulky-blue-500/30 active:scale-95 transition-all text-[10px] uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {(loading || updateLoading) ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span className="material-icons-round text-lg">{editingContact ? 'save' : 'publish'}</span>
                    {editingContact ? 'Update Entity' : 'Commit Entity'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddContactModal;
